import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import MUIDataTable from "mui-datatables";
import { Typography, Avatar } from "@mui/material";
import BasicDateRangePicker from "../../../components/common/datepicker";
import { BASE_URL } from "../../../components/config/config";
import { checkPermission } from "../../../utility/permissionUtils";

// MUI DataTable columns
const columns = [
  {
    name: "avatar",
    label: "Profile Image",
    options: {
      filter: false,
      sort: true,
      customBodyRender: (value) => (
        <Avatar alt="Profile" src={value?.url || value} />
      ),
    },
  },
  {
    name: "createdAt",
    label: "Registered Date",
    options: {
      filter: false,
      sort: true,
      customBodyRender: (value) => moment(value).format("Do MMM, hh:mm A"),
    },
  },
  {
    name: "personal_details",
    label: "Name",
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => value?.name,
    },
  },
  {
    name: "personal_details",
    label: "Email",
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => value?.email,
    },
  },
  {
    name: "referral_given",
    label: "Referral Given",
    options: {
      filter: true,
      sort: false,
      customBodyRender: (value) => (value?.length > 0 ? value.length : "0"),
    },
  },
  {
    name: "referral_received",
    label: "Referral From",
    options: {
      filter: true,
      sort: false,
      customBodyRender: (value) => (value ? "Yes" : "No One"),
    },
  },
  {
    name: "referral_code",
    label: "Referral Code",
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => value || "-",
    },
  },
  {
    name: "device_id",
    label: "Registered From",
    options: {
      filter: true,
      sort: false,
      customBodyRender: (value) => (value ? "App" : "Web"),
    },
  },
  {
    name: "address_details_permanent",
    label: "Address",
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => {
        const renderedFields = [
          value?.address_line,
          value?.city,
          value?.state,
          value?.pincode,
        ]
          .filter(Boolean)
          .join(", ");
        return renderedFields || "-";
      },
    },
  },
];

// MUI DataTable options
const options = {
  search: true,
  filter: true,
  download: false,
  print: false,
  viewColumns: false,
  selectableRows: "none",
  responsive: "standard",
};

// Inline styles
const stylesCss = {
  paddingInnerGrids: {
    paddingRight: "10px",
    paddingLeft: "10px",
    paddingTop: "2px",
  },
};

export default function UserDetailsTable() {
  const globalState = useSelector((state) => state.AdminReducer);
  const accessToken = globalState?.userTokens?.accessToken;
  const userPermissions = globalState?.adminDetails?.role_id?.permissions;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check permissions for viewing users
  const hasPermission =
    checkPermission(
      userPermissions,
      "user_details.update_user_details.view_user"
    ) || checkPermission(userPermissions, "user_details.full_access");

  useEffect(() => {
    if (hasPermission) {
      fetchData();
    }
  }, [hasPermission]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}admin/get-all-consumer`, {
        headers: {
          Authorization: accessToken,
        },
      });
      setRows(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const onClickFilter = async (value) => {
    if (!hasPermission) return;

    const payload = {
      start_date: value[0],
      end_date: value[1],
    };

    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}admin/get-all-consumer`, {
        headers: {
          Authorization: accessToken,
        },
        params: payload,
      });
      setRows(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debug permissions
  useEffect(() => {
    console.log("User Permissions:", userPermissions);
    console.log("Has Permission to view users:", hasPermission);
  }, [userPermissions, hasPermission]);

  if (!hasPermission) {
    return (
      <Typography textAlign="center" mt={4} color="error">
        You don't have permission to view user details. Please contact
        administrator.
      </Typography>
    );
  }

  return (
    <>
      <Typography variant="h4" mb={2} textAlign="center">
        Consumer Profile
      </Typography>

      <Box style={stylesCss.paddingInnerGrids}>
        <BasicDateRangePicker onClickFilter={onClickFilter} />
      </Box>

      {loading ? (
        <Typography textAlign="center" mt={4}>
          Loading user data...
        </Typography>
      ) : (
        <MUIDataTable
          options={options}
          data={rows}
          columns={columns}
          title="User Details"
        />
      )}
    </>
  );
}
